import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Images, UserSearch } from 'lucide-react';
import { useAlbumStore } from '../store/albumStore';
import { AlbumCard } from '../components/Albums/AlbumCard';
import { AlbumCardSkeleton } from '../components/UI/Skeleton';
import { MachhaCharacter } from '../lib/machhaAsset';
import { PageHeader } from '../components/UI/PageHeader';
import { TabBar, TabItem } from '../components/UI/TabBar';
import { MyPhotosTab } from '../components/Viewer/MyPhotosTab';
import { clayStaggerContainer, clayStaggerItem } from '../constants/animations';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './ViewerHomePage.module.css';

// ── Tab definitions ───────────────────────────────────────────────────────────

type TabId = 'albums' | 'my-photos';

const TABS: TabItem<TabId>[] = [
  { id: 'albums', label: 'Albums', icon: Images },
  { id: 'my-photos', label: 'My Photos', icon: UserSearch },
];

// ── Albums tab content (lifted from AlbumsPage) ───────────────────────────────

function AlbumsTabContent() {
  const { myAlbums, loading, initialized, fetchMyAlbums } = useAlbumStore();

  useEffect(() => {
    if (!initialized) fetchMyAlbums();
  }, [initialized, fetchMyAlbums]);

  if (loading || !initialized) {
    return (
      <motion.div
        className={styles['grid']}
        variants={clayStaggerContainer}
        initial="hidden"
        animate="visible"
      >
        {[0, 1, 2].map((i) => (
          <motion.div key={i} variants={clayStaggerItem}>
            <AlbumCardSkeleton />
          </motion.div>
        ))}
      </motion.div>
    );
  }

  if (myAlbums.length === 0) {
    return (
      <motion.div
        className={styles['empty-state']}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        <MachhaCharacter state="Empty" size={180} />
        <h2 className={styles['empty-title']}>No albums yet</h2>
        <p className={styles['empty-description']}>
          Albums shared with you by event creators will appear here. Join one using an invite link.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={styles['grid']}
      variants={clayStaggerContainer}
      initial="hidden"
      animate="visible"
    >
      {myAlbums.map((album) => (
        <AlbumCard key={album._id} album={album} />
      ))}
    </motion.div>
  );
}

// ── ViewerHomePage ────────────────────────────────────────────────────────────

export function ViewerHomePage() {
  usePageTitle('My Gallery');
  const [activeTab, setActiveTab] = useState<TabId>('albums');

  return (
    <div className={styles['page']}>
      <div className={styles['page-header-constrained']}>
        <PageHeader
          title="My Gallery"
          subtitle="Your albums and matched photos"
          className={styles['header-spacing']}
        />

        <TabBar<TabId>
          tabs={TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          layoutId="viewer-home-tab"
          className={styles['tab-bar']}
        />
      </div>

      <div className={styles['tab-content']}>
        {activeTab === 'albums'
          ? (
            <div className={styles['albums-content-constrained']}>
              <AlbumsTabContent />
            </div>
          )
          : <MyPhotosTab />
        }
      </div>
    </div>
  );
}
